import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Loader2, ImageIcon, Type, Heading, Video, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useSite } from "@/lib/site";
import { AssetPicker } from "./AssetPicker";

const slugify = (t) => {
  const s = t.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-");
  // keep slugs readable: cut at a word boundary, never mid-word
  return s.length > 80 ? s.slice(0, 80).replace(/-[^-]*$/, "") : s;
};

const inputCls = "w-full rounded-xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-terra";

const BlockEditor = ({ blocks, onChange }) => {
  const [pickerFor, setPickerFor] = useState(null);

  const update = (i, patch) => onChange(blocks.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const remove = (i) => onChange(blocks.filter((_, j) => j !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= blocks.length) return;
    const next = [...blocks];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const add = (type) => onChange([...blocks, { type, text: "", src: "" }]);

  return (
    <div data-testid="post-blocks-editor">
      <p className="mb-2 font-outfit text-xs font-semibold uppercase tracking-wider text-faint">Article content blocks</p>
      <div className="space-y-3">
        {blocks.map((b, i) => (
          <div key={i} data-testid={`post-block-${i}`} className="rounded-xl border border-ink/10 bg-cream/50 p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-ink/5 px-2.5 py-1 text-[10px] font-outfit font-semibold uppercase tracking-wider text-plumsoft">
                {b.type === "heading" && <Heading className="h-3 w-3" />}
                {b.type === "paragraph" && <Type className="h-3 w-3" />}
                {b.type === "image" && <ImageIcon className="h-3 w-3" />}
                {b.type === "video" && <Video className="h-3 w-3" />}
                {b.type}
              </span>
              <div className="flex items-center gap-1">
                <button data-testid={`post-block-up-${i}`} onClick={() => move(i, -1)} aria-label="Move up" className="p-1 text-faint hover:text-terra"><ArrowUp className="h-3.5 w-3.5" /></button>
                <button data-testid={`post-block-down-${i}`} onClick={() => move(i, 1)} aria-label="Move down" className="p-1 text-faint hover:text-terra"><ArrowDown className="h-3.5 w-3.5" /></button>
                <button data-testid={`post-block-delete-${i}`} onClick={() => remove(i)} aria-label="Delete block" className="p-1 text-faint hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
            {b.type === "paragraph" && (
              <textarea data-testid={`post-block-text-input-${i}`} rows={3} value={b.text || ""} onChange={(e) => update(i, { text: e.target.value })} className={inputCls} placeholder="Paragraph text" />
            )}
            {b.type === "heading" && (
              <input data-testid={`post-block-heading-input-${i}`} value={b.text || ""} onChange={(e) => update(i, { text: e.target.value })} className={inputCls} placeholder="Section heading" />
            )}
            {(b.type === "image" || b.type === "video") && (
              <div className="flex gap-2">
                <input
                  data-testid={`post-block-src-input-${i}`}
                  value={b.src || ""}
                  onChange={(e) => update(i, { src: e.target.value })}
                  className={inputCls}
                  placeholder={b.type === "image" ? "Image URL or pick from library" : "YouTube link"}
                />
                {b.type === "image" && (
                  <button type="button" data-testid={`post-block-library-${i}`} onClick={() => setPickerFor(i)} className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-ink/15 bg-white px-3 text-xs font-semibold text-ink hover:border-terra hover:text-terra">
                    <ImageIcon className="h-4 w-4" /> Library
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {[["paragraph", "Text"], ["heading", "Heading"], ["image", "Image"], ["video", "Video"]].map(([type, label]) => (
          <button
            key={type}
            data-testid={`post-add-block-${type}`}
            onClick={() => add(type)}
            className="rounded-full border border-ink/15 bg-white px-4 py-2 font-outfit text-xs font-semibold text-ink hover:border-terra hover:text-terra"
          >
            + {label}
          </button>
        ))}
      </div>
      <AssetPicker open={pickerFor !== null} onClose={() => setPickerFor(null)} onSelect={(url) => update(pickerFor, { src: url })} />
    </div>
  );
};

export const PostsTab = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const { refresh } = useSite();

  const load = () => api.get("/admin/content/posts").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    const payload = { ...editing, slug: editing.slug || slugify(editing.title || "post") };
    try {
      if (editing.id) await api.put(`/admin/content/posts/${editing.id}`, payload);
      else await api.post("/admin/content/posts", payload);
      toast.success("Article saved");
      setEditing(null);
      load();
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    await api.delete(`/admin/content/posts/${id}`);
    toast.success("Deleted");
    load();
    refresh();
  };

  return (
    <div data-testid="admin-tab-posts">
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl font-semibold text-ink">Blog articles</h2>
          <p className="mt-1 text-xs text-faint">Articles appear at /blog with SEO schema, and the latest three show on the homepage.</p>
        </div>
        <button
          data-testid="admin-posts-add"
          onClick={() => setEditing({ blocks: [{ type: "paragraph", text: "" }], published: true, published_at: new Date().toISOString().slice(0, 10) })}
          className="inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 font-outfit text-xs font-semibold text-sand hover:bg-terra"
        >
          <Plus className="h-3.5 w-3.5" /> New article
        </button>
      </div>

      {editing && (
        <div className="mb-6 rounded-2xl border border-terra/30 bg-white p-6 shadow-lift" data-testid="admin-post-form">
          <div className="mb-4 flex items-center justify-between">
            <p className="font-outfit text-sm font-semibold text-ink">{editing.id ? "Edit article" : "New article"}</p>
            <button data-testid="admin-post-form-close" onClick={() => setEditing(null)} aria-label="Close form">
              <X className="h-4 w-4 text-faint" />
            </button>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <input data-testid="admin-post-title" value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value, slug: editing.slug || slugify(e.target.value) })} className={inputCls} placeholder="Article title" />
            <input data-testid="admin-post-slug" value={editing.slug || ""} onChange={(e) => setEditing({ ...editing, slug: slugify(e.target.value) })} className={inputCls} placeholder="url-slug" />
            <textarea data-testid="admin-post-excerpt" rows={2} value={editing.excerpt || ""} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} className={`${inputCls} sm:col-span-2`} placeholder="Excerpt — shown on cards and used as the SEO description" />
            <div className="flex gap-2">
              <input data-testid="admin-post-cover" value={editing.cover_image || ""} onChange={(e) => setEditing({ ...editing, cover_image: e.target.value })} className={inputCls} placeholder="Cover image URL or pick from library" />
              <button type="button" data-testid="admin-post-cover-library" onClick={() => setPickerOpen(true)} className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-ink/15 bg-white px-3 text-xs font-semibold text-ink hover:border-terra hover:text-terra">
                <ImageIcon className="h-4 w-4" /> Library
              </button>
            </div>
            <input data-testid="admin-post-video" value={editing.video_url || ""} onChange={(e) => setEditing({ ...editing, video_url: e.target.value })} className={inputCls} placeholder="Featured YouTube video link (optional)" />
            <input data-testid="admin-post-tags" value={editing.tags || ""} onChange={(e) => setEditing({ ...editing, tags: e.target.value })} className={inputCls} placeholder="Tags (comma separated)" />
            <input data-testid="admin-post-date" type="date" value={editing.published_at || ""} onChange={(e) => setEditing({ ...editing, published_at: e.target.value })} className={inputCls} />
            <label className="flex items-center gap-2 text-sm text-plumsoft sm:col-span-2">
              <input data-testid="admin-post-published" type="checkbox" checked={editing.published !== false} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} className="h-4 w-4 accent-terra" />
              Published (visible on the website)
            </label>
          </div>
          <div className="mt-5">
            <BlockEditor blocks={editing.blocks || []} onChange={(blocks) => setEditing({ ...editing, blocks })} />
          </div>
          <button
            data-testid="admin-post-save"
            onClick={save}
            disabled={saving || !editing.title?.trim()}
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-terra px-5 py-2.5 font-outfit text-sm font-semibold text-white hover:bg-terradark disabled:opacity-60"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save article
          </button>
          <AssetPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onSelect={(url) => setEditing({ ...editing, cover_image: url })} />
        </div>
      )}

      <div className="space-y-2">
        {items.map((p, i) => (
          <div key={p.id} data-testid={`admin-post-row-${i}`} className="flex items-center gap-3 rounded-xl border border-ink/10 bg-white px-4 py-3">
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-2 truncate text-sm font-medium text-ink">
                {p.title}
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-outfit font-semibold ${p.published !== false ? "bg-sagelight text-sage" : "bg-ink/5 text-faint"}`}>
                  {p.published !== false ? "Live" : "Draft"}
                </span>
              </p>
              <p className="truncate text-xs text-faint">/blog/{p.slug}</p>
            </div>
            <button data-testid={`admin-post-edit-${i}`} onClick={() => setEditing(p)} aria-label="Edit" className="rounded-lg p-2 text-faint hover:bg-cream hover:text-terra">
              <Pencil className="h-4 w-4" />
            </button>
            <button data-testid={`admin-post-delete-${i}`} onClick={() => remove(p.id)} aria-label="Delete" className="rounded-lg p-2 text-faint hover:bg-red-50 hover:text-red-500">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        {items.length === 0 && <p className="py-8 text-center text-sm text-faint">No articles yet — write the first one.</p>}
      </div>
    </div>
  );
};
